import type { NodeDefinition } from "@/generated";
import { GLOBAL_VARIABLE_ICON_DATA_URI } from "@/icons/DataTypeIcon";
import type { DynamicNodeConfig } from "@/store/editor-v2";
import type { GraphNodeV2 } from "@/types/spotflow-v2";
import type { Edge } from "@xyflow/react";
import { getNodeIconBgHex } from "@/components/SpotflowV2/nodeCategories";
import { parseFlowInputType } from "@/components/SpotflowV2/flowTypes";
import { inputConsumesRefsWhole } from "@/components/SpotflowV2/configRefExpansion";
import {
  formatPathVarLabel,
  pathKeysFor,
  pathRowsFor,
  type PathValueRow,
} from "./pathValues";
import {
  isSmartRouteNode,
  routeHandlesFromConfig,
  isMergePathsNode,
  mergeInputHandlesFromConfig,
} from "./smartRouteBindings";
import type { SecretScope, SecretSource } from "./useSecretsSources";
import { nodeIconName } from "@/components/SpotflowV2/subflowIcon";
import {
  getCompatibility,
  getInlineCompatibility,
  type Compatibility,
} from "./typeCompatibility";
import {
  normalizeVariableRefId,
  parseVarRefId,
  WHOLE_VAR_REF_PATTERN,
} from "./variableRefs";

interface V2ObjectFieldSpec {
  name?: string;
  label?: string | null;
  data_type?: string;
  is_list?: boolean | null;
}

// A V2 node output — the clean shape served by the backend (NodeOutputV2).
// No connectors: just a named, typed value the node produces. `list_depth`
// gives the nesting depth (0 scalar, 1 list, 2 list-of-lists, …); `is_list` is
// the legacy boolean kept in sync (is_list == list_depth >= 1).
interface V2OutputSpec {
  name?: string;
  label?: string;
  data_type?: string;
  object_fields?: V2ObjectFieldSpec[] | null;
  is_list?: boolean;
  list_depth?: number;
}

export interface ObjectField {
  name: string;
  label: string | null;
  dataType: string;
  isList: boolean;
}

export interface OutputOption {
  value: string;
  outputName: string;
  outputLabel: string;
  outputOriginalLabel: string;
  dataType: string;
  isList: boolean;
  listDepth: number;
  compatible: boolean;
  // "coerced" = converted on resolve (object → JSON text, number/bool/datetime
  // → plain text); badged, not hidden.
  compatibility: Compatibility;
  scope?: SecretScope;
  objectFields?: ObjectField[];
  // Set for declared path variables (`nodeId.<path>.<name>`): the path key
  // the variable is scoped to. Consumers use it to tell path options apart
  // (e.g. auto-bind reconciliation must never pick them up).
  pathKey?: string;
}

export interface NodeOption {
  nodeId: string;
  nodeName: string;
  nodeColor: string;
  nodeImage?: string | null;
  nodeIconName?: string;
  outputs: OutputOption[];
  hasCompatible: boolean;
}

export type OutputMeta = {
  dataType: string;
  isList: boolean;
  listDepth: number;
};

// Nesting depth from a spec, tolerating specs that only set the legacy
// `is_list` boolean (dynamic get_config outputs, un-regenerated types).
function specDepth(isList: boolean, listDepth?: number): number {
  if (typeof listDepth === "number") return Math.max(listDepth, isList ? 1 : 0);
  return isList ? 1 : 0;
}

type OutputMetaResolver = (
  sourceNodeId: string,
  outputName: string
) => { dataType: string; listDepth: number } | null;

/** Element type + depth of a declared path variable — mirror of the backend
 * projection rule (`_path_var_type` in flow_v2/validation.py — change them
 * together): an iterating node projects the ref per item, so the channel is a
 * list even when the ref is a scalar (`max(refDepth, 1)`); a whole-run node's
 * mask is scalar, so the value passes through at its own depth (`refDepth`).
 * Literal / interpolated rows are str, following the same depth rule. */
function pathVarMeta(
  row: PathValueRow,
  pathNodeIterates: boolean,
  outputMetaFor: OutputMetaResolver
): { dataType: string; listDepth: number } {
  const whole = row.value.match(WHOLE_VAR_REF_PATTERN);
  const parsed = whole ? parseVarRefId(normalizeVariableRefId(whole[1])) : null;
  if (!parsed) return { dataType: "str", listDepth: pathNodeIterates ? 1 : 0 };
  const meta = outputMetaFor(parsed.sourceNodeId, parsed.outputName);
  const refDepth = meta?.listDepth ?? 0;
  return {
    dataType: meta?.dataType ?? "str",
    listDepth: pathNodeIterates ? Math.max(refDepth, 1) : refDepth,
  };
}

export function getUpstreamNodeDistances(
  targetNodeId: string,
  edges: Edge[]
): Map<string, number> {
  const reverseAdj = new Map<string, string[]>();
  for (const edge of edges) {
    const targets = reverseAdj.get(edge.target) ?? [];
    targets.push(edge.source);
    reverseAdj.set(edge.target, targets);
  }

  const distances = new Map<string, number>();
  const queue: Array<{ id: string; depth: number }> = [
    { id: targetNodeId, depth: 0 },
  ];
  const visited = new Set<string>([targetNodeId]);
  while (queue.length > 0) {
    const { id: current, depth } = queue.shift()!;
    for (const source of reverseAdj.get(current) ?? []) {
      if (!visited.has(source)) {
        visited.add(source);
        distances.set(source, depth + 1);
        queue.push({ id: source, depth: depth + 1 });
      }
    }
  }
  return distances;
}

/** Whether an edge from `sourceId`'s source handle `handleId` leads to the
 * current node (directly or through any of its upstream ancestors) — the
 * scoping rule for path-carried variables: they only exist downstream of
 * their path's handle. */
export function handleReachesCurrent(
  sourceId: string,
  handleId: string,
  currentNodeId: string,
  edges: Edge[],
  upstreamDistances: Map<string, number>
): boolean {
  return edges.some(
    (e) =>
      e.source === sourceId &&
      (e.sourceHandle ?? "output") === handleId &&
      (e.target === currentNodeId || upstreamDistances.has(e.target))
  );
}

function nodesOnConnectedPath(
  currentNodeId: string,
  handle: string,
  edges: Edge[]
): Set<string> {
  const allowed = new Set<string>();
  for (const e of edges) {
    if (e.target !== currentNodeId || (e.targetHandle ?? null) !== handle) {
      continue;
    }
    allowed.add(e.source);
    for (const up of getUpstreamNodeDistances(e.source, edges).keys()) {
      allowed.add(up);
    }
  }
  return allowed;
}

export function inputPathHandleFor(
  nodes: GraphNodeV2[],
  currentNodeId: string,
  nodeRegistry: NodeDefinition[],
  inputName: string | undefined
): string | undefined {
  if (!inputName) return undefined;
  const current = nodes.find((n) => n.id === currentNodeId);
  if (isMergePathsNode(current?.data.nodeType)) {
    const handles = mergeInputHandlesFromConfig(current?.data.nodeConfig).map(
      (h) => h.id
    );
    return handles.includes(inputName) ? inputName : undefined;
  }
  const reg = nodeRegistry.find((r) => r.type === current?.data.nodeType);
  const inputPaths =
    (reg as { input_paths?: string[] } | undefined)?.input_paths ?? [];
  return inputPaths.includes(inputName) ? inputName : undefined;
}

export function buildVariableOptions({
  nodes,
  currentNodeId,
  edges,
  nodeRegistry,
  inputDataType,
  inputIsList,
  inputListDepth,
  dynamicNodeConfigs,
  secrets,
  restrictToHandle,
  inputName,
  inlineText,
}: {
  nodes: GraphNodeV2[];
  currentNodeId: string;
  edges: Edge[];
  nodeRegistry: NodeDefinition[];
  inputDataType: string | undefined;
  inputIsList?: boolean;
  inputListDepth?: number;
  dynamicNodeConfigs: Map<string, DynamicNodeConfig>;
  secrets: SecretSource[];
  restrictToHandle?: string;
  /** Field being bound, so the picker can tell whether that field would ever
   * spread a list source across runs. Nested operands address their field by
   * its first segment (`advanced_filters.0.value`). */
  inputName?: string;
  /** True when the field is inline text (prompts, code, condition prompts…):
   * refs are interpolated, so compatibility follows `getInlineCompatibility`
   * instead of the typed whole-field table, and the depth/iteration gates
   * don't apply. Whether a File may be embedded is the current node's
   * `accepts_inline_files`, never a property of the field. */
  inlineText?: boolean;
}): NodeOption[] {
  const inputDepth = specDepth(!!inputIsList, inputListDepth);
  const currentNode = nodes.find((n) => n.id === currentNodeId);
  const currentDef = currentNode
    ? nodeRegistry.find((r) => r.type === currentNode.data.nodeType)
    : undefined;
  const inlineAcceptsFiles = currentDef?.accepts_inline_files ?? false;
  const compatibilityFor = (
    outputType: string | undefined,
    outputDepth: number
  ): Compatibility =>
    inlineText
      ? getInlineCompatibility(outputType, inlineAcceptsFiles)
      : getCompatibility(outputType, inputDataType, outputDepth, inputDepth);
  const compatFields = (
    outputType: string | undefined,
    outputDepth: number
  ): Pick<OutputOption, "compatible" | "compatibility"> => {
    const compatibility = compatibilityFor(outputType, outputDepth);
    return { compatible: compatibility !== "incompatible", compatibility };
  };
  const upstreamDistances = getUpstreamNodeDistances(currentNodeId, edges);
  const pathScope = restrictToHandle
    ? nodesOnConnectedPath(currentNodeId, restrictToHandle, edges)
    : null;

  // Element type + depth of any node's output (incl. the source's own iterate
  // lift) — feeds path-variable typing, which may reference ANY node's output
  // (upstream pass-through or the path node's own).
  const outputMetaFor: OutputMetaResolver = (sourceNodeId, outputName) => {
    const source = nodes.find((x) => x.id === sourceNodeId);
    if (!source) return null;
    const sourceReg = nodeRegistry.find((r) => r.type === source.data.nodeType);
    const override = dynamicNodeConfigs.get(sourceNodeId);
    const rawOutputs = (override?.outputs ??
      sourceReg?.outputs ??
      []) as V2OutputSpec[];
    const spec = rawOutputs.find((o) => o.name === outputName);
    if (!spec) return null;
    const config = source.data.nodeConfig as
      | Record<string, unknown>
      | undefined;
    const isInput =
      source.data.nodeType === "InputNode" ||
      source.data.nodeType === "InputNodeV2";
    const configType = config?.type as string | undefined;
    const parsedType = parseFlowInputType(configType);
    const dataType = isInput
      ? configType
        ? parsedType.dataType
        : spec.data_type || "str"
      : (spec.data_type ?? "object");
    const baseDepth = isInput
      ? specDepth(
          !!config?.is_list || parsedType.isList,
          config?.list_depth as number | undefined
        )
      : specDepth(!!spec.is_list, spec.list_depth);
    return {
      dataType,
      listDepth: baseDepth + (source.data.iterate ? 1 : 0),
    };
  };

  const currentSupportsIteration =
    (currentDef as { supports_iteration?: boolean } | undefined)
      ?.supports_iteration !== false;
  // The field's own say: a container display that consumes its refs whole never
  // fans a list out, whatever the node supports.
  const currentConsumesRefsWhole = inputConsumesRefsWhole(
    (
      currentDef as
        | { config?: Record<string, { display?: { type?: string } }> }
        | undefined
    )?.config,
    inputName
  );

  // The current node is never a source — not even for its own path-values
  // rows: config refs resolve against the pool before the node runs, so a
  // self-ref (`:var[<self>.reasoning]`) yields None, and the node's outputs
  // are already reachable on every one of its paths downstream.
  const nodeOptions: NodeOption[] = nodes
    .filter(
      (n) =>
        n.id !== currentNodeId &&
        upstreamDistances.has(n.id) &&
        (!pathScope || pathScope.has(n.id))
    )
    .map((n) => {
      const reg = nodeRegistry.find((r) => r.type === n.data.nodeType);
      const override = dynamicNodeConfigs.get(n.id);
      // V2 outputs are clean specs: { name, label?, data_type?, is_list? }.
      // Dynamic config (from the node's get_config endpoint) overrides the
      // static registry outputs; there are no connectors or per-key expansion.
      const rawOutputs = (override?.outputs ??
        reg?.outputs ??
        []) as V2OutputSpec[];

      const nodeConfig = n.data.nodeConfig as
        | Record<string, unknown>
        | undefined;
      const nodeOutputLabels = (nodeConfig?.__output_labels ?? {}) as Record<
        string,
        string
      >;
      const isInputNode =
        n.data.nodeType === "InputNode" || n.data.nodeType === "InputNodeV2";
      const inputNodeName = isInputNode
        ? (nodeConfig?.label as string | undefined)
        : undefined;

      const configType = nodeConfig?.type as string | undefined;
      const parsedConfigType = parseFlowInputType(configType);
      const configIsList = !!nodeConfig?.is_list || parsedConfigType.isList;
      const configListDepth = nodeConfig?.list_depth as number | undefined;
      // A node in iteration mode runs once per item and its outputs are
      // consolidated into a list — so each output gains one nesting level.
      const nodeIterates = !!n.data.iterate;

      const outputs: OutputOption[] = rawOutputs
        .filter((o) => !!o.name)
        .map((o) => {
          const name = o.name as string;
          // undefined = unknown, never blocks; `dataType` keeps the legacy
          // "object" fallback for display/type-target writes.
          const declaredType = isInputNode
            ? configType
              ? parsedConfigType.dataType
              : o.data_type || "str"
            : o.data_type;
          const dataType = declaredType ?? "object";
          const originalLabel = o.label || name;
          const baseDepth = isInputNode
            ? specDepth(configIsList, configListDepth)
            : specDepth(!!o.is_list, o.list_depth);
          const listDepth = baseDepth + (nodeIterates ? 1 : 0);
          // A source nested deeper than the field declares fits only if something
          // will spread it. Iteration does that for an ordinary field; for one
          // that consumes its refs whole nothing does, so the depths must match
          // and the backend's "needs a single value" is never reached.
          const compatibility: Compatibility = inlineText
            ? getInlineCompatibility(declaredType, inlineAcceptsFiles)
            : (currentSupportsIteration || listDepth < 1) &&
                (!currentConsumesRefsWhole || listDepth <= inputDepth)
              ? getCompatibility(
                  declaredType,
                  inputDataType,
                  listDepth,
                  inputDepth
                )
              : "incompatible";
          const objectFields = (o.object_fields ?? [])
            .filter((f) => !!f.name)
            .map((f) => ({
              name: f.name as string,
              label: f.label ?? null,
              dataType: f.data_type ?? "str",
              isList: !!f.is_list,
            }));
          return {
            value: `${n.id}.${name}`,
            outputName: name,
            outputLabel:
              nodeOutputLabels[name] || inputNodeName || originalLabel,
            outputOriginalLabel: originalLabel,
            dataType,
            isList: listDepth >= 1,
            listDepth,
            compatible: compatibility !== "incompatible",
            compatibility,
            ...(objectFields.length > 0 ? { objectFields } : {}),
          };
        });

      // Declared path variables: a multi-path node's `path_values` rows
      // materialize as aligned path-scoped variables (`nodeId.<path>.<name>`,
      // always lists of only the items that took the path). Offered only to
      // nodes downstream of that path's handle — anywhere else the variable
      // always resolves to Skipped. Raw outputs above stay offered everywhere
      // (they are pure: full-length, positionally aligned).
      const pathInfos = pathKeysFor(n.data.nodeType, reg, nodeConfig);
      if (pathInfos.length > 0) {
        const rowsByPath = pathRowsFor(nodeConfig);
        for (const info of pathInfos) {
          const rows = (rowsByPath[info.key] ?? []).filter((r) => r.name);
          if (rows.length === 0) continue;
          if (
            !handleReachesCurrent(
              n.id,
              info.handleId,
              currentNodeId,
              edges,
              upstreamDistances
            )
          ) {
            continue;
          }
          for (const row of rows) {
            const meta = pathVarMeta(row, !!n.data.iterate, outputMetaFor);
            const label = formatPathVarLabel(info.label, row.name);
            outputs.push({
              value: `${n.id}.${info.key}.${row.name}`,
              outputName: `${info.key}.${row.name}`,
              outputLabel: label,
              outputOriginalLabel: label,
              dataType: meta.dataType,
              isList: true,
              listDepth: meta.listDepth,
              pathKey: info.key,
              ...compatFields(meta.dataType, meta.listDepth),
            });
          }
        }
      }

      // Smart Route's per-route branches are routing handles rather than
      // static outputs, and each also carries the chosen route's value, so
      // they stay readable as `nodeId.<routeId>` labelled by the route name.
      // Only branches that actually lead to the current node are offered —
      // a sibling branch is inactive whenever this one runs.
      if (isSmartRouteNode(n.data.nodeType)) {
        const branchesToCurrent = new Set<string>();
        for (const e of edges) {
          if (e.source !== n.id || !e.sourceHandle) continue;
          if (e.target === currentNodeId || upstreamDistances.has(e.target)) {
            branchesToCurrent.add(e.sourceHandle);
          }
        }
        for (const handle of routeHandlesFromConfig(nodeConfig)) {
          if (!branchesToCurrent.has(handle.id)) continue;
          outputs.push({
            value: `${n.id}.${handle.id}`,
            outputName: handle.id,
            outputLabel: handle.label,
            outputOriginalLabel: handle.label,
            dataType: "str",
            isList: false,
            listDepth: 0,
            ...compatFields("str", 0),
          });
        }
      }

      // The On-error path's built-in error details stay bindable directly
      // (`nodeId.on_error`), scoped like any path variable.
      if ((nodeConfig?.on_error_action as string | undefined) === "skip") {
        if (
          handleReachesCurrent(
            n.id,
            "on_error",
            currentNodeId,
            edges,
            upstreamDistances
          )
        ) {
          outputs.push({
            value: `${n.id}.on_error`,
            outputName: "on_error",
            outputLabel: "Error details",
            outputOriginalLabel: "Error details",
            dataType: "str",
            isList: false,
            listDepth: 0,
            ...compatFields("str", 0),
          });
        }
      }

      const compatRank: Record<Compatibility, number> = {
        ok: 0,
        coerced: 1,
        incompatible: 2,
      };
      outputs.sort(
        (a, b) => compatRank[a.compatibility] - compatRank[b.compatibility]
      );
      return {
        nodeId: n.id,
        nodeName: n.data.name,
        nodeColor: getNodeIconBgHex({
          type: n.data.nodeType,
          category: reg?.category ?? null,
        }),
        nodeImage: n.data.image,
        nodeIconName: nodeIconName(n.data.nodeType, n.data.nodeConfig),
        outputs,
        hasCompatible: outputs.some((o) => o.compatible),
      };
    })
    .sort((a, b) => {
      const dist = (id: string) =>
        id === currentNodeId ? 0 : (upstreamDistances.get(id) ?? 999);
      return dist(a.nodeId) - dist(b.nodeId);
    });

  if (secrets.length === 0) return nodeOptions;

  // Workspace + workflow variables/secrets are bindable via the `__secrets__`
  // pseudo-source: value is the full ref (`__secrets__.<scope>:<id>`), the
  // output name is the qualified id (`<scope>:<id>`) the resolver decodes.
  const secretOutputs: OutputOption[] = secrets.map((s) => ({
    value: s.ref,
    outputName: s.ref.slice(s.ref.indexOf(".") + 1),
    outputLabel: s.name,
    outputOriginalLabel: s.name,
    dataType: s.dataType,
    isList: false,
    listDepth: 0,
    scope: s.scope,
    ...compatFields(s.dataType, 0),
  }));
  const secretsNode: NodeOption = {
    nodeId: "__secrets__",
    nodeName: "Variables",
    nodeColor: "#e0e7ff",
    nodeImage: GLOBAL_VARIABLE_ICON_DATA_URI,
    outputs: secretOutputs,
    hasCompatible: secretOutputs.some((o) => o.compatible),
  };
  return [...nodeOptions, secretsNode];
}

export function buildOptionsByValue(
  options: NodeOption[]
): Map<string, { node: NodeOption; output: OutputOption }> {
  const values = new Map<string, { node: NodeOption; output: OutputOption }>();
  for (const node of options) {
    for (const output of node.outputs) {
      values.set(output.value, { node, output });
    }
  }
  return values;
}
